import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import type { CSSProperties, Ref } from 'react'
import { Avatar, userColorVar } from '../shell/Avatar.tsx'
import { EnterHighlight } from '../../ui/EnterHighlight.tsx'
import { useRemoteChange } from '../../hooks/useRealtime.tsx'
import { CommentCount, DueDate, LabelChip, PriorityMeter } from '../../ui/primitives.tsx'
import type { Task, User } from '../../lib/types.ts'
import styles from './board.module.css'

/**
 * docs/ui-spec.md §5.2, "Anatomia karty". Order is fixed: title, labels, then the meta footer.
 * Density closer to Linear than to Trello.
 *
 * Purely presentational, so the same card can render in the table's mobile list where there is no
 * DndContext to hang useSortable off. Dragging lives in SortableTaskCard below — one look, two
 * mounting points, no second implementation (RULES §5).
 */
export function TaskCard({
  task,
  assignee,
  dimmed = false,
  dragging = false,
  onOpen,
  nodeRef,
  style,
  dragProps,
}: {
  task: Task
  assignee: User | null
  dimmed?: boolean
  dragging?: boolean
  onOpen: (id: number) => void
  nodeRef?: Ref<HTMLButtonElement>
  style?: CSSProperties
  dragProps?: Record<string, unknown>
}): React.JSX.Element {
  const flash = useRemoteChange('tasks', task.id)

  const className = [styles.card, dimmed ? styles.cardDim : '', dragging ? styles.cardDragging : '']
    .filter(Boolean)
    .join(' ')

  return (
    <button
      ref={nodeRef}
      type="button"
      className={className}
      style={style}
      onClick={() => onOpen(task.id)}
      {...dragProps}
    >
      <EnterHighlight active={flash} />
      <div className={styles.cardTitle}>{task.title}</div>

      {task.labels.length > 0 && (
        <div className={styles.cardLabels}>
          {task.labels.map((label) => (
            <LabelChip key={label}>{label}</LabelChip>
          ))}
        </div>
      )}

      <div className={styles.cardMeta}>
        <PriorityMeter priority={task.priority} />
        {/* Data dodania siedzi przy ref, nie przy terminie. Obie po prawej wyglądałyby identycznie
            i myliły się co chwilę; tu czyta się jako „skąd to jest", a termin zostaje sam po prawej
            jako „na kiedy to jest". */}
        <span className={styles.cardRef}>
          {task.ref}
          <span className={styles.cardDot}>·</span>
          {formatShortDate(task.createdAt)}
        </span>
        {/* Styl `.agentChip` istniał od etapu 2 i nigdy nie był renderowany — pola
            `createdByAgent` po prostu nie było. Zadanie od agenta wyglądało tak samo jak każde
            inne, mimo że PRD §4.4 wprost chce je odróżnić. */}
        {task.createdByAgent && (
          <span className={styles.agentChip} title="Utworzone przez agenta przez MCP">
            mcp
          </span>
        )}
        <CommentCount count={task.commentCount} />
        <span className={styles.cardSpacer} />
        {task.dueDate && <DueDate date={task.dueDate} />}
        {assignee ? (
          <Avatar name={assignee.displayName} colorVar={userColorVar(assignee.id)} size={20} />
        ) : null}
      </div>
    </button>
  )
}

/** `dd.MM` — short enough to sit next to the ref without turning the footer into a second row. */
function formatShortDate(iso: string): string {
  const [, month, day] = iso.slice(0, 10).split('-')
  return `${day}.${month}`
}

/** The board's card: the same component, wired to dnd-kit. */
export function SortableTaskCard(props: {
  task: Task
  assignee: User | null
  dimmed?: boolean
  onOpen: (id: number) => void
}): React.JSX.Element {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: props.task.id,
  })

  return (
    <TaskCard
      {...props}
      dragging={isDragging}
      nodeRef={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      dragProps={{ ...attributes, ...listeners }}
    />
  )
}
